import React, { useState } from 'react';
import CardImage from './CardImage';

function RecommendationResults({ data }) {
  const [expandedCard, setExpandedCard] = useState(null);

  if (!data || !data.recommendations || data.recommendations.length === 0) {
    return (
      <div className="text-center py-6">
        <p className="text-sm text-on-surface-variant">No offers found for this merchant</p>
      </div>
    );
  }

  const bestCard = data.recommendations[0];
  const otherCards = data.recommendations.slice(1);
  const purchaseAmount = data.purchase_amount || 100;

  const getCardDisplayName = (rec) => {
    if (rec.card_name) return rec.card_name;
    const card = rec.card;
    if (card.cardName) return card.cardName;
    if (card.nickname) return card.nickname;
    return `${card.network} ${card.bank}`;
  };

  const formatReturn = (totalValue, amount) => {
    const pct = ((totalValue / amount) * 100).toFixed(1);
    return `${pct}%`;
  };

  const getValueTypeLabel = (valueType) => {
    const labels = {
      issuer_offer: 'Issuer Offer',
      credit: 'Statement Credit',
      category_bonus: 'Category Bonus',
      partner_program: 'Partner Program',
      base_rate: 'Base Rewards',
    };
    return labels[valueType] || 'Rewards';
  };

  const renderValueBreakdown = (rec) => {
    const breakdown = rec.value_breakdown;
    if (!breakdown) return null;

    const items = [];

    if (breakdown.points) {
      const p = breakdown.points;
      items.push({
        label: `${p.multiplier}x ${p.category_bonus ? 'category' : 'base'} points`,
        value: `$${p.value_dollars.toFixed(2)}`,
        detail: p.note,
        highlight: p.category_bonus,
      });
    }

    if (breakdown.credits && breakdown.credits.applicable_credits) {
      breakdown.credits.applicable_credits.forEach((credit) => {
        items.push({
          label: credit.description,
          value: `$${credit.value.toFixed(0)}`,
          detail: credit.note,
          highlight: true,
        });
      });
    }

    if (breakdown.issuer_offer) {
      items.push({
        label: 'Issuer Offer',
        value: breakdown.issuer_offer.value,
        detail: breakdown.issuer_offer.description,
        highlight: true,
      });
    }

    if (breakdown.partner_benefits && breakdown.partner_benefits.applicable_programs) {
      breakdown.partner_benefits.applicable_programs.forEach((prog) => {
        if (prog.value > 0) {
          items.push({
            label: prog.program,
            value: `$${prog.value.toFixed(0)}`,
            detail: prog.description,
            highlight: true,
          });
        }
      });
    }

    return (
      <div className="mt-3 bg-surface-container-low rounded-xl p-3 space-y-2">
        <p className="text-[11px] font-semibold text-on-surface-variant/70 uppercase tracking-label">Value Breakdown</p>
        {items.map((item, idx) => (
          <div key={idx} className={`flex flex-col ${item.highlight ? 'bg-primary-light/40 -mx-1 px-1 py-1 rounded-lg' : ''}`}>
            <div className="flex justify-between items-center">
              <span className="text-sm text-on-surface">{item.label}</span>
              <span className="text-sm font-semibold text-on-surface">{item.value}</span>
            </div>
            {item.detail && <span className="text-xs text-on-surface-variant">{item.detail}</span>}
          </div>
        ))}
        <div className="flex justify-between items-center pt-2 border-t border-outline-variant/30">
          <span className="text-sm font-semibold text-on-surface">Total Value</span>
          <span className="text-sm font-bold text-primary">
            ${rec.total_value?.toFixed(2) || rec.valueScore?.toFixed(2)}
          </span>
        </div>
      </div>
    );
  };

  const renderInfoSection = (title, items) => {
    if (!items || items.length === 0) return null;
    return (
      <div className="mt-3">
        <p className="text-[11px] font-semibold text-on-surface-variant/70 uppercase tracking-label mb-1">{title}</p>
        <ul className="space-y-1">
          {items.map((item, idx) => (
            <li key={idx} className="text-xs text-on-surface-variant flex gap-1.5">
              <span className="text-primary mt-0.5">•</span>
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  const hasEqualValue = !!data.equal_value_message;

  return (
    <div>
      {/* Equal Value Message */}
      {hasEqualValue && (
        <div className="bg-primary-light px-4 py-3">
          <p className="text-sm text-primary font-medium">{data.equal_value_message}</p>
        </div>
      )}

      {/* Best Card */}
      <div className="bg-gradient-to-br from-primary to-primary-dark p-4 text-white">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className="material-symbols-outlined filled text-warning" style={{ fontSize: 18 }}>
              lightbulb
            </span>
            <span className="text-xs font-semibold uppercase tracking-wider opacity-90">
              {hasEqualValue ? 'Your Cards' : 'Optimal Strategy'}
            </span>
          </div>
          <div className="text-right">
            <span className="text-xl font-bold">
              ${bestCard.total_value?.toFixed(2) || bestCard.valueScore?.toFixed(2)}
            </span>
            <span className="block text-xs opacity-80">
              {formatReturn(bestCard.total_value || bestCard.valueScore, purchaseAmount)} return
            </span>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <CardImage card={bestCard.card} size="medium" />
          <div>
            <p className="font-semibold text-sm">{getCardDisplayName(bestCard)}</p>
            <p className="text-xs opacity-80">
              {bestCard.card.network} • {bestCard.card.bank}
            </p>
            <span className="inline-block mt-1 px-2 py-0.5 bg-white/20 rounded-full text-xs">
              {getValueTypeLabel(bestCard.valueType)}
            </span>
          </div>
        </div>
      </div>

      {/* Best Card Details */}
      <div className="p-4 space-y-0">
        {bestCard.explanation?.short && (
          <div className="bg-success-light rounded-xl p-3">
            <p className="text-sm text-on-surface">{bestCard.explanation.short}</p>
          </div>
        )}

        {renderValueBreakdown(bestCard)}
        {renderInfoSection('Required Actions', bestCard.required_actions)}
        {renderInfoSection('Assumptions', bestCard.assumptions)}
        {renderInfoSection('Note', bestCard.uncertainties)}

        {bestCard.benefits && bestCard.benefits.length > 0 && (
          <div className="mt-3">
            <p className="text-[11px] font-semibold text-on-surface-variant/70 uppercase tracking-label mb-1">
              Benefits Applied
            </p>
            <ul className="space-y-1">
              {bestCard.benefits.map((benefit, index) => (
                <li key={index} className="text-xs text-on-surface flex gap-1.5">
                  <span className="text-success">✓</span>
                  <span>{benefit}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Other Cards */}
      {otherCards.length > 0 && (
        <div className="border-t border-outline-variant/30">
          <h3 className="text-sm font-semibold text-on-surface-variant px-4 pt-3 pb-2">Other Options</h3>
          {otherCards.map((rec, index) => {
            const isExpanded = expandedCard === index;
            return (
              <div key={index} className="border-t border-outline-variant/20">
                <button
                  className="w-full flex items-center gap-3 p-4 text-left hover:bg-surface-container-low transition-colors"
                  onClick={() => setExpandedCard(isExpanded ? null : index)}
                >
                  <CardImage card={rec.card} size="small" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-on-surface truncate">{getCardDisplayName(rec)}</p>
                    <p className="text-xs text-on-surface-variant">
                      {rec.card.network} • {rec.card.bank}
                    </p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-sm font-bold text-on-surface">
                      ${rec.total_value?.toFixed(2) || rec.valueScore?.toFixed(2)}
                    </p>
                    <p className="text-xs text-on-surface-variant">
                      {formatReturn(rec.total_value || rec.valueScore, purchaseAmount)}
                    </p>
                  </div>
                  <span className="material-symbols-outlined text-on-surface-variant text-lg">
                    {isExpanded ? 'expand_less' : 'expand_more'}
                  </span>
                </button>

                {rec.comparison_to_best && (
                  <p className="text-xs text-on-surface-variant px-4 -mt-2 mb-2">{rec.comparison_to_best}</p>
                )}

                {rec.explanation?.short && (
                  <p className="text-xs text-on-surface-variant px-4 pb-2">{rec.explanation.short}</p>
                )}

                {isExpanded && (
                  <div className="px-4 pb-4">
                    {renderValueBreakdown(rec)}
                    {renderInfoSection('Required Actions', rec.required_actions)}
                    {renderInfoSection('Assumptions', rec.assumptions)}
                    {renderInfoSection('Note', rec.uncertainties)}
                    {rec.benefits && rec.benefits.length > 0 && (
                      <div className="mt-3">
                        <p className="text-[11px] font-semibold text-on-surface-variant/70 uppercase tracking-label mb-1">Benefits</p>
                        <ul className="space-y-1">
                          {rec.benefits.map((benefit, i) => (
                            <li key={i} className="text-xs text-on-surface flex gap-1.5">
                              <span className="text-success">✓</span>
                              <span>{benefit}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Summary */}
      <div className="bg-gradient-to-r from-primary/5 to-primary/10 p-4">
        {hasEqualValue ? (
          <p className="text-sm text-on-surface">
            <strong>Recommendation:</strong> Any of your cards will work equally well at {data.merchant}
            {data.category && <span className="text-primary"> ({data.category})</span>}
          </p>
        ) : (
          <p className="text-sm text-on-surface">
            <strong>Recommendation:</strong> Use <strong>{getCardDisplayName(bestCard)}</strong> for{' '}
            <strong className="text-primary">
              ${bestCard.total_value?.toFixed(2) || bestCard.valueScore?.toFixed(2)}
            </strong>{' '}
            total value at {data.merchant}
            {data.category && <span className="text-primary"> ({data.category})</span>}
          </p>
        )}
        {!hasEqualValue && bestCard.summary && (
          <p className="text-xs text-on-surface-variant mt-1">{bestCard.summary}</p>
        )}
      </div>
    </div>
  );
}

export default RecommendationResults;
